import { useCallback, useEffect, useState } from 'react'
import { DryRunReduceResult } from '@kunalabs-io/kai'
import { useCurrentClient } from '@mysten/dapp-kit-react'
import Decimal from 'decimal.js'

import { PositionInfo } from '@/shared/kai/hooks/useUserPositions'

import { getProtocolDenyList, SwapType } from './useWithdrawDetails'
import { useRouter } from 'contexts/SwapRouterContext'
import { useCurrentWalletAddress } from 'utils/useCurrentWalletAddress'

interface Args {
  debouncedAmountPercentage: number
  positionInfo: PositionInfo
  swapType: SwapType
  slippagePct: number
  overestimationPct: number
  afProtocols: Record<string, boolean>
  cetusProtocols: Record<string, boolean>
}
export const useDryRunData = ({
  debouncedAmountPercentage,
  positionInfo,
  swapType,
  slippagePct,
  overestimationPct,
  afProtocols,
  cetusProtocols,
}: Args) => {
  const walletAddress = useCurrentWalletAddress()
  const client = useCurrentClient()
  const { routerLabel, router } = useRouter()

  const [isLoadingDryRun, setIsLoadingDryRun] = useState(false)
  const [dryRunError, setDryRunError] = useState<Error>()
  const [dryRunResult, setDryRunResult] = useState<DryRunReduceResult>()

  useEffect(() => {
    const calculateDryRun = async () => {
      if (!walletAddress || debouncedAmountPercentage === 0) {
        return undefined
      }

      try {
        setIsLoadingDryRun(true)
        setDryRunError(undefined)
        setDryRunResult(undefined)
        const factor = new Decimal(debouncedAmountPercentage).div(100)
        const dryRunResult = await positionInfo.lastPosition.dryRunReduce(
          client,
          router,
          {
            factor,
            positionCapId: positionInfo.positionCapId,
            routerArgs:
              swapType === 'exact-in'
                ? {
                    swapMethod: swapType,
                    slippage: slippagePct / 100,
                    overestimationFactor: overestimationPct / 100,
                    protocolDenylist: getProtocolDenyList(routerLabel, afProtocols, cetusProtocols),
                  }
                : {
                    swapMethod: swapType,
                    slippage: slippagePct / 100,
                    protocolDenylist: getProtocolDenyList(routerLabel, afProtocols, cetusProtocols),
                  },
          },
          walletAddress
        )

        setDryRunResult(dryRunResult)
      } catch (e) {
        if (e instanceof Error) {
          setDryRunError(e)
        }
        console.log('Error: ', e)
      } finally {
        setIsLoadingDryRun(false)
      }
    }

    void calculateDryRun()
  }, [
    afProtocols,
    cetusProtocols,
    client,
    debouncedAmountPercentage,
    overestimationPct,
    positionInfo.lastPosition,
    positionInfo.positionCapId,
    router,
    routerLabel,
    slippagePct,
    swapType,
    walletAddress,
  ])

  const resetDryRunResult = useCallback(() => {
    setDryRunError(undefined)
    setDryRunResult(undefined)
  }, [])

  return {
    isLoadingDryRun,
    dryRunResult,
    dryRunError,
    resetDryRunResult,
  }
}
